
import IconEdit from "@/components/common/icon/IconEdit.vue";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import Pagination from "@/components/common/Pagination.vue";
import { IClient, IClientParams } from "@/entities";
import {
  EDirection,
  IClientsReportRequest,
  IInformationOptionsClients,
  IPageResponse,
} from "@/services";
import VueFilters from "@/vue-filters";
import { BvTableFieldArray } from "bootstrap-vue";
import { Component, Vue } from "vue-property-decorator";
import { Action } from "vuex-class";
import ClientsFilter from "@/components/client/ClientsFilter.vue";
import { EventBus } from "@/event-bus";
import { Dictionary } from "vue-router/types/router";
import { i18n } from "@/i18n";
import { SweetAlertResult } from "sweetalert2";

@Component({
  name: "clients-table",
  mixins: [VueFilters],
  components: {
    ClientsFilter,
    IconEdit,
    IconTrash,
    Pagination,
  },
})
export default class ClientsTable extends Vue {
  @Action("fetchClientsPage") fetchClientsPage!: (
    params: IClientParams
  ) => Promise<IPageResponse<IClient> | undefined>;
  @Action("updateStatusClient") updateStatusClient!: (id: number) => Promise<void>;
  @Action("generateClientsReport") generateClientsReport!: (
    request: IClientsReportRequest
  ) => Promise<void>;

  private clients: IPageResponse<IClient> | undefined = {
    content: [] as IClient[],
  } as IPageResponse<IClient>;

  private queryParams = this.$route.query;

  private params: IClientParams = {
    active: this.$ability.can("read", "ClientEditActive")
      ? this.queryParams.active !== "false"
      : true,
    codeSol: this.queryParams.codeSol?.toString(),
    countryId: this.queryParams.countryId ? Number(this.queryParams.countryId) : undefined,
    description: this.queryParams.description?.toString(),
    idSmartcash: this.queryParams.idSmartcash?.toString(),
    codePrecise: this.queryParams.codePrecise?.toString() === "true" || false,
    descriptionPrecise: this.queryParams.descriptionPrecise?.toString() === "true" || false,
    idCashPrecise: this.queryParams.idCashPrecise?.toString() === "true" || false,
  };
  private pageNumber = Number(this.queryParams.page) || 1;
  private pageSize = 10;
  private totalElements = 0;
  private orderBy = this.queryParams.orderBy?.toString() || "id";
  private sortDesc = this.queryParams.direction !== "ASC";

  constructor() {
    super();
  }

  mounted(): void {
    this.getClients();
    EventBus.$on("update-clients-table", () => {
      this.onChangeCurrentPage(1);
    });
  }

  async beforeDestroy(): Promise<void> {
    EventBus.$off("update-clients-table");
  }

  async onChangeCurrentPage(currentPage: number): Promise<void> {
    this.pageNumber = currentPage;
    await this.getClients();
  }

  async filterClients(): Promise<void> {
    this.$nextTick(async () => this.getClients());
  }

  async getClients(): Promise<void> {
    this.params.size = this.pageSize;
    this.params.orderBy = this.orderBy || undefined;
    this.params.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
    //Para que en la url aparezca la pagina + 1
    this.params.page = this.pageNumber;
    await this.$router
      .replace({ path: this.$route.path, query: this.params as Dictionary<string> })
      .catch(() => {
        // do nothing.
      });
    this.params.page = this.pageNumber - 1;

    this.clients = await this.fetchClientsPage(this.params);

    this.totalElements = this.clients?.totalElements || 0;
  }

  get showInactive(): boolean {
    return !this.params.active;
  }

  set showInactive(value: boolean) {
    this.params.active = !value;
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
      },
      {
        key: "country.code",
        label: this.$t("column.country") as string,
        sortable: true,
        thClass: this.$ability.can("read", "Countries") ? "table-header" : "table-header d-none",
        tdClass: this.$ability.can("read", "Countries")
          ? "table-cell-middle"
          : "table-cell-middle d-none",
        formatter: (value: string | undefined) => this.$t(`country.${value?.toLowerCase()}`),
      },
      {
        key: "codeSol",
        label: this.$t("column.code") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "description",
        label: this.$t("column.description") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "active",
        label: this.$t("column.active") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thAttr: this.$ability.can("read", "ClientEditActive") ? null : { hidden: true },
        tdAttr: this.$ability.can("read", "ClientEditActive") ? null : { hidden: true },
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.confirmation.to.export.message").toString(),
      text: this.$t("label.confirmation.to.export.message.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  async updateStatusClientLocal(client: IClient): Promise<void> {
    client.active = !client.active;
    await this.updateStatusClient(client.id!);
    this.getClients();
  }

  get serviceClientsReportParams(): IClientsReportRequest {
    return {
      cashClientParamsDTO: this.params,
      informationOptionsClient: this.informationOptionsClients,
    } as IClientsReportRequest;
  }
  private async executeReportClient(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      await this.generateClientsReport(this.serviceClientsReportParams);
      this.makeToast("success", this.$t("message.usersreport.ok").toString());
    }
  }
  private get informationOptionsClients(): IInformationOptionsClients {
    return {
      country: true,
      active: true,
      entity: true,
      entityDescription: true,
    } as IInformationOptionsClients;
  }
  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
